import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["display", "editForm", "input", "submit", "cancel", "loadingIcon"]
    static values = {
        format: { type: String, default: "text" },
        currency: { type: Boolean, default: false }
    }

    declare displayTarget: HTMLElement
    declare editFormTarget: HTMLElement
    declare inputTarget: HTMLInputElement
    declare submitTarget: HTMLButtonElement
    declare cancelTarget: HTMLButtonElement
    declare loadingIconTarget: HTMLElement
    declare initialValue: string
    declare formatValue: string
    declare currencyValue: boolean
    private rawValue: string = ""

    connect() {
        this.initialValue = this.inputTarget.value
        this.rawValue = this.initialValue
        this.updateDisplay()
        
        // Add input event listener for number formatting
        if (this.currencyValue) {
            this.inputTarget.addEventListener('input', this.formatNumberInput.bind(this))
        }
    }

    disconnect() {
        if (this.currencyValue) {
            this.inputTarget.removeEventListener('input', this.formatNumberInput.bind(this))
        }
    }

    formatNumberInput(event: Event) {
        const input = event.target as HTMLInputElement
        // Get cursor position
        const cursorPosition = input.selectionStart || 0
        const oldLength = input.value.length
        
        // Remove all non-numeric characters except decimal point
        let value = input.value.replace(/[^\d.]/g, '')
        
        // Ensure only one decimal point
        const parts = value.split('.')
        if (parts.length > 2) {
            value = parts[0] + '.' + parts.slice(1).join('')
        }
        
        // Store the raw value without commas
        this.rawValue = value
        
        // Format the whole number part with commas for display
        const wholeNumber = parts[0]
        const formattedWholeNumber = wholeNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        
        // Combine with decimal part if it exists
        const formattedValue = parts.length > 1 ? formattedWholeNumber + '.' + parts[1] : formattedWholeNumber
        
        // Update the input value with formatted display
        input.value = formattedValue
        
        // Adjust cursor position
        const newLength = formattedValue.length
        const newPosition = cursorPosition + (newLength - oldLength)
        input.setSelectionRange(newPosition, newPosition)
    }

    formatValueWithCommas(value: string): string {
        // Remove any existing commas
        const cleanValue = value.replace(/,/g, '')
        const parts = cleanValue.split('.')
        
        // Format the whole number part with commas
        const wholeNumber = parts[0]
        const formattedWholeNumber = wholeNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        
        // Combine with decimal part if it exists
        return parts.length > 1 ? formattedWholeNumber + '.' + parts[1] : formattedWholeNumber
    }

    startEditing() {
        this.displayTarget.classList.add('hidden')
        this.editFormTarget.classList.remove('hidden')
        
        // Format the initial value with commas if it's a currency field
        if (this.currencyValue && this.inputTarget.value) {
            this.inputTarget.value = this.formatValueWithCommas(this.inputTarget.value)
        }
        
        this.inputTarget.focus()
        this.submitTarget.classList.remove('hidden')
        this.cancelTarget.classList.remove('hidden')
    }

    cancelEditing() {
        this.displayTarget.classList.remove('hidden')
        this.editFormTarget.classList.add('hidden')
        this.inputTarget.value = this.initialValue
        this.rawValue = this.initialValue
        this.submitTarget.classList.add('hidden')
        this.cancelTarget.classList.add('hidden')
    }

    submitForm(event: Event) {
        event.preventDefault()
        
        // Create a hidden input to store the raw value
        if (this.currencyValue) {
            const hiddenInput = document.createElement('input')
            hiddenInput.type = 'hidden'
            hiddenInput.name = this.inputTarget.name
            hiddenInput.value = this.rawValue
            this.element.appendChild(hiddenInput)
            
            // Disable the visible input to prevent it from being submitted
            this.inputTarget.disabled = true
        }
        
        this.submitTarget.classList.add('hidden')
        this.cancelTarget.classList.add('hidden')
        this.loadingIconTarget.classList.remove('hidden')
        ;(this.element as HTMLFormElement).requestSubmit()
    }

    private updateDisplay() {
        const value = this.currencyValue ? this.rawValue : this.inputTarget.value
        if (this.currencyValue) {
            this.displayTarget.textContent = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
            }).format(Number(value))
        } else if (this.formatValue === 'number') {
            this.displayTarget.textContent = new Intl.NumberFormat('en-US').format(Number(value))
        } else {
            this.displayTarget.textContent = value
        }
    }
} 